import { useEffect, useRef, useState } from "react";

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export const useAnimatedCounter = ({
  start,
  end,
  duration = 2000,
  enabled = true,
}) => {
  const ref = useRef(null);
  const [value, setValue] = useState(start);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setValue(end);
      return;
    }

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasAnimated.current) return;
        hasAnimated.current = true;

        const range = end - start;
        const startTime = performance.now();

        const tick = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = easeOutCubic(progress);
          const next = Math.round(start + range * eased);
          setValue(next);

          if (progress < 1) {
            requestAnimationFrame(tick);
          } else {
            setValue(end);
          }
        };

        requestAnimationFrame(tick);
      },
      { threshold: 0.2, rootMargin: "0px 0px -5% 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [start, end, duration, enabled]);

  return { ref, value };
};
